import "typeahead.js/dist/typeahead.jquery";
import { View } from "extensions/marionette";
import { captureException } from "extensions/sentry";
import { Backbone } from "extensions/backbone";
import { _ } from "extensions/underscore";

import strings from "common/strings";

import _template from "./typeahead.jst";

import "./typeahead.scss";

export { default as Bloodhound } from "typeahead.js/dist/bloodhound";

export const Source = Backbone.Model.extend({
    defaults: {
        display: "",
        limit: 6,
        templates_suggestion: (data) => `<div><strong>${data.alias}</strong> – ${data.type}</div>`,
        templates_empty: (obj) => `<div class='empty-message'>${strings.preferences.typeahead.emptyMessage}</div>`
    },
    initialize() {
        if (!this.has("source")) {
            throw new Error("Source data is required for the model");
        }
    },
    toJSON() {
        return {
            display: this.get("display"),
            source: this.get("source"),
            limit: this.get("limit"),
            templates: {
                empty: this.get("templates_empty"),
                suggestion: this.get("templates_suggestion")
            }
        };
    }
});

export const Sources = Backbone.Collection.extend({
    model: Source,
    getList() {
        const sources = this.toJSON();
        
        // Passing in `null` for the `options` arguments will result in the default options being used
        return [null].concat(sources);
    }
});

export const TypeaheadView = View.extend({
    className: "typeahead-cont",
    template: _template,
    ui: {
        input: "input",
        add: "button.add"
    },
    events: {
        "click @ui.add": "manualSelect"
    },
    
    /**
     * Initialize typeahead library on our text input
     */
    initTypeahead() {
        const args = this.collection.getList();
        
        this.ui.input.typeahead(...args);
        
        // Bind events
        this.typeaheadEvents = {
            "typeahead:active": _.bind(this.active, this),
            "typeahead:select": _.bind(this.e.select, this),
            "typeahead:idle": _.bind(this.e.idle, this),
            "typeahead:autocomplete": _.bind(this.e.autocomplete, this),
            "typeahead:render": _.bind(this.e.checkEmpty, this),
            keypress: _.bind(this.e.keypress, this)
        };
        
        this.ui.input.on(this.typeaheadEvents);
    },
    
    /**
     * Get text value from the typeahead control
     * @returns {*}
     */
    typeaheadTextValue() {
        return this.ui.input.typeahead("val").trim();
    },
    
    _getTypeaheadObject() {
        return this.ui.input.data("ttTypeahead");
    },
    
    // TWITTER TYPEAHEAD EVENTS
    e: {
        /**
         * EVENT: idle
         * Fires when user is not interacting with the control anymore
         */
        idle(e) {
            // If input contains a value then don't collapse the component. otherwise close it
            const value = this.typeaheadTextValue();
            if (value.length === 0) {
                this.close();
            }
        },
        
        /**
         * EVENT: Autocomplete
         * @param e
         * @param obj
         */
        autocomplete(e, obj) {
            this.ui.add.prop("disabled", false);
            this.value = obj;
        },
        
        select(e, obj) {
            this.value = obj;
            this.selected();
        },
        
        keypress(e) {
            if (e.which === 13 && this.value) {
                this.selected();
            }
        },
        
        checkEmpty(e, found) {
            // Check if the menu has found items and if it is then don't show not_found text
            let suggestions;
            let $menu;
            
            // TODO: Fragile implementation
            try {
                $menu = this._getTypeaheadObject().menu.$node;
                suggestions = $menu.find(".tt-suggestion").length;
            } catch (error) {
                // Typeahead version changed. Update logic to find suggestions above
                // assume we do have suggestions, so the UI doesn't break
                captureException(error);
                suggestions = 1;
            }
    
            // Hide all notFound messages
            $menu.find(".empty-message").addClass("hidden");
    
            // Only show the first message if there are zero results available
            if (suggestions === 0) {
                $menu.find(".empty-message").first().removeClass("hidden");
            }
        }
    },
    
    focus() {
        if (_.isFunction(this.ui.input.focus)) {
            this.ui.input.focus();
        }
    },
    
    /**
     * EVENT: User manually clicks ADD button
     * @param e
     */
    manualSelect(e) {
        if (this.ui.add.prop("disabled")) {
            return false;
        }
        
        if (this.value) {
            this.selected();
        }
    },
    
    selected() {
        const obj = this.value;
        this.trigger("selected", obj);
        this.close();
    },
    
    /**
     * Routine when control is active
     */
    active() {
        this.$el.addClass("expanded");
    },
    
    /**
     * Routine when control becomes inactive
     */
    close() {
        if (this.isDestroyed()) {
            return;
        }
        
        // Nullify values
        this.value = null;
        this.ui.input.typeahead("val", "");
        
        // Remove focus from the control
        this.ui.input.blur();
        
        // Collapse the control
        this.$el.removeClass("expanded");
        
        // Make add button disabled
        this.ui.add.prop("disabled", true);
    },
    
    unbindEvents() {
        if (this.typeaheadEvents) {
            this.ui.input.off();
            this.typeaheadEvents = null;
        }
    },
    
    destroyTypeahead() {
        if (this.ui.input && this.ui.input.typeahead) {
            this.ui.input.typeahead("destroy");
        }
        this.unbindEvents();
    },
    
    onBeforeDestroy() {
        this.destroyTypeahead();
    },
    
    onAttach() {
        this.initTypeahead();
        
        if (this.model.get("autofocus")) {
            // Autofocus input field
            window.setTimeout(() => this.focus(), 200);
        }
    }
});
